import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CourseDetail, QuizAttempt, QuizResult } from '@grc/types';
import { apiFetch, ApiRequestError } from '../../lib/api.js';
import { useOrg } from '../../lib/auth.js';
import {
  Badge,
  Button,
  Card,
  LoadingPane,
  PageHeader,
  ProgressBar,
  formatDate,
  useToast,
} from '../../components/ui.js';
import { Markdown } from '../../components/Markdown.js';
import './training.css';

const QUIZ_STEP = '__quiz__';

export function CoursePage() {
  const { courseId = '' } = useParams();
  const { org } = useOrg();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const detail = useQuery({
    queryKey: ['training-course', org.orgId, courseId],
    queryFn: () => apiFetch<CourseDetail>(`/orgs/${org.orgId}/training/courses/${courseId}`),
  });

  const [selected, setSelected] = useState<string | null>(null);
  const [attempt, setAttempt] = useState<QuizAttempt | null>(null);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [result, setResult] = useState<QuizResult | null>(null);
  const contentHeadingRef = useRef<HTMLHeadingElement>(null);
  const focusOnNextStep = useRef(false);

  // A different course or a different org is a different learning context —
  // drop any in-flight quiz state instead of carrying it across.
  useEffect(() => {
    setSelected(null);
    setAttempt(null);
    setAnswers({});
    setResult(null);
  }, [org.orgId, courseId]);

  const data = detail.data;
  const completedSet = useMemo(
    () => new Set(data?.progress.completedModuleIds ?? []),
    [data?.progress.completedModuleIds],
  );
  const allModulesDone = !!data && data.modules.every((m) => completedSet.has(m.id));

  // Land on the first incomplete module (or the quiz when everything is read).
  useEffect(() => {
    if (!data || selected !== null) return;
    const next = data.modules.find((m) => !completedSet.has(m.id));
    setSelected(next ? next.id : QUIZ_STEP);
  }, [data, selected, completedSet]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['training-course', org.orgId, courseId] });
    void queryClient.invalidateQueries({ queryKey: ['training-courses', org.orgId] });
  };

  const completeModule = useMutation({
    mutationFn: (moduleId: string) =>
      apiFetch(`/orgs/${org.orgId}/training/courses/${courseId}/modules/${moduleId}/complete`, {
        method: 'POST',
      }),
    onSuccess: (_progress, moduleId) => {
      invalidate();
      if (!data) return;
      const index = data.modules.findIndex((m) => m.id === moduleId);
      const next = data.modules[index + 1];
      focusOnNextStep.current = true;
      setSelected(next ? next.id : QUIZ_STEP);
    },
    onError: (err) =>
      toast(err instanceof ApiRequestError ? err.message : 'Could not save progress', 'error'),
  });

  const startQuiz = useMutation({
    mutationFn: () =>
      apiFetch<QuizAttempt>(`/orgs/${org.orgId}/training/courses/${courseId}/quiz`, {
        method: 'POST',
      }),
    onSuccess: (a) => {
      setAttempt(a);
      setAnswers({});
      setResult(null);
    },
    onError: (err) =>
      toast(err instanceof ApiRequestError ? err.message : 'Could not start the quiz', 'error'),
  });

  const submitQuiz = useMutation({
    mutationFn: () =>
      apiFetch<QuizResult>(`/orgs/${org.orgId}/training/quiz-attempts/${attempt!.id}/answers`, {
        method: 'POST',
        body: {
          answers: Object.entries(answers).map(([questionId, selectedIndex]) => ({
            questionId,
            selectedIndex,
          })),
        },
      }),
    onSuccess: (r) => {
      setResult(r);
      setAttempt(null);
      focusOnNextStep.current = true;
      invalidate();
    },
    onError: (err) =>
      toast(err instanceof ApiRequestError ? err.message : 'Could not submit answers', 'error'),
  });

  // Move focus to the newly-shown step heading after user-triggered
  // transitions (module complete / quiz submit), never on initial load.
  useEffect(() => {
    if (focusOnNextStep.current) {
      focusOnNextStep.current = false;
      contentHeadingRef.current?.focus();
    }
  }, [selected, result]);

  if (detail.isLoading) return <LoadingPane label="Loading course" />;
  if (detail.isError) {
    const notFound =
      detail.error instanceof ApiRequestError && detail.error.status === 404;
    return (
      <div>
        <PageHeader
          title={notFound ? 'Course not found' : 'Could not load this course'}
          subtitle={notFound ? undefined : 'Please check your connection and try again.'}
        />
        <p>
          <Link to="/training/my-courses">Back to my courses</Link>
        </p>
      </div>
    );
  }
  if (!data) return null;

  const selectedModule = data.modules.find((m) => m.id === selected) ?? null;
  const quizSelected = selected === QUIZ_STEP;
  const completed = data.progress.status === 'completed';

  return (
    <div>
      <PageHeader
        title={data.course.title}
        subtitle={data.course.description}
        actions={<Link to="/training/my-courses">← My courses</Link>}
      />

      <div className="course-progress-row">
        <ProgressBar percent={data.progress.percent} label="Course progress" />
        <span className="course-progress-note">
          {data.progress.completedSteps}/{data.progress.steps} steps · {data.course.durationMinutes}{' '}
          mins
        </span>
        {completed && !data.progress.expired && (
          <Badge tone="success">Completed — expires {formatDate(data.progress.expiresAt)}</Badge>
        )}
        {completed && !data.progress.expired && data.progress.attested && (
          <Badge tone="info">
            Attested
            {data.progress.attestation ? ` — ${data.progress.attestation.source}` : ''}
          </Badge>
        )}
        {data.progress.expired && <Badge tone="warning">Expired — retake the final quiz</Badge>}
      </div>

      <div className="course-layout">
        <nav className="course-outline" aria-label="Course modules">
          <ol>
            {data.modules.map((module, index) => (
              <li key={module.id}>
                <button
                  type="button"
                  className={`outline-step${selected === module.id ? ' active' : ''}${completedSet.has(module.id) ? ' done' : ''}`}
                  onClick={() => setSelected(module.id)}
                >
                  <span className="outline-index" aria-hidden="true">
                    {completedSet.has(module.id) ? '✓' : index + 1}
                  </span>
                  {module.title}
                </button>
              </li>
            ))}
            <li>
              <button
                type="button"
                className={`outline-step outline-quiz${quizSelected ? ' active' : ''}${completed ? ' done' : ''}`}
                onClick={() => setSelected(QUIZ_STEP)}
              >
                <span className="outline-index" aria-hidden="true">
                  {completed ? '✓' : '★'}
                </span>
                Final Quiz
              </button>
            </li>
          </ol>
        </nav>

        <div className="course-content">
          {selectedModule && (
            <Card>
              <h2 className="module-title" tabIndex={-1} ref={contentHeadingRef}>
                {selectedModule.title}
              </h2>
              <Markdown markdown={selectedModule.contentMd} />
              <div className="module-actions">
                {completedSet.has(selectedModule.id) ? (
                  <Badge tone="success">Module completed</Badge>
                ) : (
                  <Button
                    onClick={() => completeModule.mutate(selectedModule.id)}
                    disabled={completeModule.isPending}
                  >
                    Mark as complete &amp; continue
                  </Button>
                )}
              </div>
            </Card>
          )}

          {quizSelected && (
            <Card>
              <h2 className="module-title" tabIndex={-1} ref={contentHeadingRef}>
                Final Quiz
              </h2>
              {result ? (
                <QuizResultPanel
                  result={result}
                  retakePending={startQuiz.isPending}
                  onRetake={() => startQuiz.mutate()}
                />
              ) : attempt ? (
                <div className="quiz">
                  <p className="quiz-intro">
                    Answer every question. You need all {attempt.questions.length} correct to
                    complete the course.
                  </p>
                  <ol className="quiz-questions">
                    {attempt.questions.map((q, qi) => (
                      <li key={q.id}>
                        <fieldset>
                          <legend>{q.text}</legend>
                          {q.options.map((option, oi) => (
                            <label key={oi} className="quiz-option">
                              <input
                                type="radio"
                                name={`q-${qi}`}
                                checked={answers[q.id] === oi}
                                onChange={() => setAnswers((a) => ({ ...a, [q.id]: oi }))}
                              />
                              {option}
                            </label>
                          ))}
                        </fieldset>
                      </li>
                    ))}
                  </ol>
                  <Button
                    onClick={() => submitQuiz.mutate()}
                    disabled={
                      submitQuiz.isPending ||
                      Object.keys(answers).length < attempt.questions.length
                    }
                  >
                    Submit answers
                  </Button>
                </div>
              ) : (
                <div>
                  <p>
                    The final quiz draws a random selection of questions covering at least half of
                    the course modules (from a bank of {data.questionBankSize}). Answer them all
                    correctly to complete the course
                    {completed ? ' again and refresh your 12-month completion.' : '.'}
                  </p>
                  {!allModulesDone && !completed ? (
                    <p className="quiz-locked">Complete all modules to unlock the final quiz.</p>
                  ) : (
                    <Button onClick={() => startQuiz.mutate()} disabled={startQuiz.isPending}>
                      {completed ? 'Retake final quiz' : 'Start final quiz'}
                    </Button>
                  )}
                </div>
              )}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function QuizResultPanel({
  result,
  retakePending,
  onRetake,
}: {
  result: QuizResult;
  retakePending: boolean;
  onRetake: () => void;
}) {
  return (
    <div className="quiz-result">
      {result.passed ? (
        <>
          <Badge tone="success">Passed</Badge>
          <p className="quiz-result-headline">
            {result.score}/{result.total} correct — course completed. Your completion is valid
            until {formatDate(result.progress.expiresAt)}.
          </p>
        </>
      ) : (
        <>
          <Badge tone="danger">Not passed</Badge>
          <p className="quiz-result-headline">
            {result.score}/{result.total} correct. Review the explanations below, revisit the
            modules and try again — each attempt draws a fresh set of questions.
          </p>
          <ul className="quiz-explanations">
            {result.results
              .filter((r) => !r.correct)
              .map((r) => (
                <li key={r.questionId}>{r.explanation}</li>
              ))}
          </ul>
          <Button onClick={onRetake} disabled={retakePending}>
            Try again
          </Button>
        </>
      )}
    </div>
  );
}
